import {
    categoryLayoutVariantDefs,
    ONE_DRAW_DEFAULT_MAXFILL_CATEGORY,
} from '../../../../services/layoutVariantPrompts';
import { isValidSnapshotDataUrl } from '../../../../helpers/layoutSnapshotCapture';
import { useViewerStore } from '../../../../store/store';
import {
    collectCategoryCandidates,
    formatCategoryWinnerDisplayLabel,
    getCategoryMainLayoutKey,
    isVariantOnlyCategory,
    physicalLayoutSignature,
    rankCategoryCandidates,
    resolveLayoutKeyForCategory,
    roofFaceIdSetsEqual,
    type CategoryLayoutCandidate,
} from './utils/ai-layout';
import { countModulesInLayout, logCategoryWinnersAudit, logGeminiRoofCombinations, logAllAiDesignCardData } from './utils/ai-layout';
import { canonicalCategoryName } from './utils/ai-layout';

type CategoryVariant = {
    id?: string;
    snapshotUrl?: string;
    snapshotCaptureError?: string;
    productionW?: number;
};

function enrichBestSnapshot(
    category: string,
    best: CategoryLayoutCandidate,
    state: any,
): CategoryLayoutCandidate {
    const variants = (state.aiCategoryLayoutVariants?.[category] ?? []) as CategoryVariant[];
    const trySnap = (url: unknown): string | undefined =>
        isValidSnapshotDataUrl(url as string) ? (url as string) : undefined;

    let snapshotUrl = trySnap(best.snapshotUrl);
    if (!snapshotUrl && best.variantId) {
        snapshotUrl = trySnap(variants.find((v) => v.id === best.variantId)?.snapshotUrl);
    }
    if (!snapshotUrl && typeof best.production === 'number' && Number.isFinite(best.production)) {
        const targetProd = Math.round(best.production);
        snapshotUrl = trySnap(
            variants.find(
                (v) =>
                    trySnap(v.snapshotUrl) &&
                    typeof v.productionW === 'number' &&
                    Number.isFinite(v.productionW) &&
                    Math.round(v.productionW) === targetProd,
            )?.snapshotUrl,
        );
    }
    if (!snapshotUrl) {
        snapshotUrl = trySnap(variants.find((v) => trySnap(v.snapshotUrl))?.snapshotUrl);
    }
    if (!snapshotUrl && best.key) {
        snapshotUrl = trySnap(state.layoutSnapshotsByKey?.[best.key]);
    }

    if (snapshotUrl) {
        return { ...best, snapshotUrl, snapshotError: undefined };
    }

    const variantErr = variants.find(
        (v) => typeof v.snapshotCaptureError === 'string' && v.snapshotCaptureError.trim(),
    )?.snapshotCaptureError;
    const snapshotError =
        (typeof best.snapshotError === 'string' && best.snapshotError.trim()) ||
        (typeof variantErr === 'string' && variantErr.trim()) ||
        (best.modules > 0
            ? 'Layout snapshot was not captured for this category'
            : 'No modules on layout — snapshot skipped');

    return { ...best, snapshotUrl: undefined, snapshotError };
}

type ApplyCategoryBestLayoutsArgs = {
    setAiLoadingStep?: (step: string) => void;
    roofIdsByLayoutKey: (key: string) => string[];
    roofCombinationsFromAi?: Array<{ name: string; roofFaceIds?: string[] }>;
};

/** A candidate is "faithful" only if it carries the exact placement we can re-apply 1:1. */
function hasExactLayout(c: CategoryLayoutCandidate): boolean {
    return Array.isArray(c.modulesLayout) && c.modulesLayout.length > 0;
}

function getCategoryRoofFaceIds(
    category: string,
    roofCombinationsFromAi: Array<{ name: string; roofFaceIds?: string[] }>,
): string[] {
    const layoutKey = resolveLayoutKeyForCategory(category, roofCombinationsFromAi);
    const target = canonicalCategoryName(layoutKey);
    const combo = roofCombinationsFromAi.find(
        (c) => canonicalCategoryName(c.name) === target,
    );
    return combo?.roofFaceIds?.filter(Boolean) ?? [];
}

function pickCategoryWinner(
    category: string,
    candidates: CategoryLayoutCandidate[],
    usedPhysicalSignatures: Set<string>,
    roofCombinationsFromAi: Array<{ name: string; roofFaceIds?: string[] }>,
): CategoryLayoutCandidate | undefined {
    const ranked = rankCategoryCandidates(category, candidates);
    if (!ranked.length) return undefined;

    // Only candidates with an exact captured placement (`modulesLayout`) can be reproduced
    // faithfully on apply, so prefer them. Candidates built from the stale `__category_main__`
    // maps / default combo lack a layout and pair production/modules from one roof set with
    // roof IDs from another — that is exactly what made "Max Production with Best Roofs" show
    // default-maxfill metrics (29 mod) while applying a 4-roof subset (23 mod). Fall back to
    // the full ranking only when nothing has a captured layout.
    const faithful = ranked.filter(hasExactLayout);
    const pool = faithful.length ? faithful : ranked;

    const isAlreadyUsed = (c: CategoryLayoutCandidate) =>
        usedPhysicalSignatures.has(physicalLayoutSignature(c));

    if (isVariantOnlyCategory(category)) {
        const best = pool[0];
        usedPhysicalSignatures.add(physicalLayoutSignature(best));
        return best;
    }

    const categoryRoofIds = getCategoryRoofFaceIds(category, roofCombinationsFromAi);

    const pickFromPool = (list: CategoryLayoutCandidate[]): CategoryLayoutCandidate | undefined => {
        const unused = list.find((c) => !isAlreadyUsed(c));
        if (unused) return unused;
        if (categoryRoofIds.length) {
            return list.find((c) => roofFaceIdSetsEqual(c.roofIds, categoryRoofIds));
        }
        return undefined;
    };

    const distinct = pickFromPool(pool);
    const best = distinct ?? pickFromPool(ranked);
    if (!best) return undefined;

    usedPhysicalSignatures.add(physicalLayoutSignature(best));
    return best;
}

/** Rank all category candidates and write winning main-card metrics into the viewer store. */
export function applyCategoryBestLayoutsAction(args: ApplyCategoryBestLayoutsArgs): void {
    const { setAiLoadingStep, roofIdsByLayoutKey, roofCombinationsFromAi = [] } = args;
    const state = useViewerStore.getState() as any;

    setAiLoadingStep?.('Selecting best layout per category...');

    logGeminiRoofCombinations(roofCombinationsFromAi);

    const nextProduction = { ...(state.layoutProductionByKey ?? {}) };
    const nextModules = { ...(state.layoutModulesByKey ?? {}) };
    const nextMetrics = { ...(state.layoutMetricsByKey ?? {}) };
    const nextSnapshots = { ...(state.layoutSnapshotsByKey ?? {}) };
    const nextModulesLayoutByKey = { ...(state.layoutModulesLayoutByKey ?? {}) };
    const categoryMainLayoutByCategory: Record<string, CategoryLayoutCandidate> = {};
    const usedWinnerPhysicalSignatures = new Set<string>();

    for (const category of Object.keys(categoryLayoutVariantDefs)) {
        const candidates = collectCategoryCandidates({
            category,
            variantsByCategory: state.aiCategoryLayoutVariants ?? {},
            layoutMetricsByKey: state.layoutMetricsByKey ?? {},
            layoutProductionByKey: state.layoutProductionByKey ?? {},
            layoutModulesByKey: state.layoutModulesByKey ?? {},
            layoutSnapshotsByKey: state.layoutSnapshotsByKey ?? {},
            layoutModulesLayoutByKey: state.layoutModulesLayoutByKey ?? {},
            roofIdsByLayoutKey,
            roofCombinationsFromAi,
            // Re-derive the winner from fresh candidates only (variants + simulated combos).
            // Reusing the previous winner / stale __category_main__ map values can pair
            // production/modules from one generation with roof IDs from another, which made
            // "Max Production with Best Roofs" show default-maxfill metrics on the card while
            // applying a different (subset-roof) layout.
            excludeRecycledMainCandidate: true,
        });
        const rawBest = pickCategoryWinner(
            category,
            candidates,
            usedWinnerPhysicalSignatures,
            roofCombinationsFromAi,
        );
        if (!rawBest) continue;

        const best = enrichBestSnapshot(category, rawBest, state);
        const catKey = getCategoryMainLayoutKey(category);
        // The card module count MUST equal what the apply will place. When we have an exact
        // captured layout, derive `modules` from it so the card can never disagree with the
        // 3D viewer (the layout is the single source of truth).
        const exactModuleCount = countModulesInLayout(best.modulesLayout);
        const winnerModules = exactModuleCount > 0 ? exactModuleCount : best.modules;
        categoryMainLayoutByCategory[category] = {
            ...best,
            modules: winnerModules,
            displayLabel: formatCategoryWinnerDisplayLabel(category, best),
        };
        if (best.modulesLayout?.length) {
            nextModulesLayoutByKey[catKey] = best.modulesLayout;
            const comboKey = resolveLayoutKeyForCategory(category, roofCombinationsFromAi);
            if (comboKey && comboKey !== catKey) {
                nextModulesLayoutByKey[comboKey] = best.modulesLayout;
            }
            nextModulesLayoutByKey[category] = best.modulesLayout;
        }
        nextProduction[catKey] = best.production;
        nextModules[catKey] = winnerModules;
        nextMetrics[catKey] = {
            efficiency: best.efficiency,
            solarAccess: best.solarAccess,
            multiplier: best.multiplier,
            treeCount: best.treeCount ?? null,
            roofCount:
                typeof best.roofCount === 'number' && Number.isFinite(best.roofCount) && best.roofCount > 0
                    ? best.roofCount
                    : null,
        };
        if (best.snapshotUrl) {
            nextSnapshots[catKey] = best.snapshotUrl;
        }

        // Main catalog card reads layout key `default` — keep it in sync with the best
        // all-roof maxfill orientation (portrait / landscape / mixed).
        if (category === ONE_DRAW_DEFAULT_MAXFILL_CATEGORY) {
            nextProduction.default = best.production;
            nextModules.default = winnerModules;
            nextMetrics.default = {
                efficiency: best.efficiency,
                solarAccess: best.solarAccess,
                multiplier: best.multiplier,
                treeCount: best.treeCount ?? null,
                roofCount:
                    typeof best.roofCount === 'number' &&
                    Number.isFinite(best.roofCount) &&
                    best.roofCount > 0
                        ? best.roofCount
                        : null,
            };
            if (best.snapshotUrl) {
                nextSnapshots.default = best.snapshotUrl;
            }
            if (best.modulesLayout?.length) {
                nextModulesLayoutByKey.default = best.modulesLayout;
            }
        }
    }

    const prevMain = (state.categoryMainLayoutByCategory ?? {}) as Record<string, CategoryLayoutCandidate>;
    const nextMainKeys = Object.keys(categoryMainLayoutByCategory);
    const winnersUnchanged =
        nextMainKeys.length > 0 &&
        nextMainKeys.length === Object.keys(prevMain).length &&
        nextMainKeys.every((cat) => {
            const prev = prevMain[cat];
            const next = categoryMainLayoutByCategory[cat];
            if (!prev || !next) return false;
            return prev.key === next.key && prev.variantId === next.variantId && prev.production === next.production;
        });
    const metricsAlreadyWritten =
        winnersUnchanged &&
        nextMainKeys.every((cat) => {
            const catKey = getCategoryMainLayoutKey(cat);
            const best = categoryMainLayoutByCategory[cat];
            const metrics = state.layoutMetricsByKey?.[catKey];
            return (
                state.layoutProductionByKey?.[catKey] === best.production &&
                state.layoutModulesByKey?.[catKey] === best.modules &&
                metrics?.efficiency === best.efficiency &&
                metrics?.solarAccess === best.solarAccess
            );
        });
    if (metricsAlreadyWritten) {
        useViewerStore.setState({
            layoutSnapshotsByKey: nextSnapshots,
            layoutModulesLayoutByKey: nextModulesLayoutByKey,
            categoryMainLayoutByCategory,
        } as any);
        logCategoryWinnersAudit();
        logAllAiDesignCardData();
        return;
    }

    useViewerStore.setState({
        layoutProductionByKey: nextProduction,
        layoutModulesByKey: nextModules,
        layoutMetricsByKey: nextMetrics,
        layoutSnapshotsByKey: nextSnapshots,
        layoutModulesLayoutByKey: nextModulesLayoutByKey,
        categoryMainLayoutByCategory,
    } as any);
    logCategoryWinnersAudit();
    logAllAiDesignCardData();
}
